import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PersonIcon from '@mui/icons-material/Person';
import {
  Box,
  Chip,
  Collapse,
  Divider,
  IconButton,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { SurgicalProcedureCoverage } from '../../../types';

interface SurgicalProcedureRowProps {
  procedure: SurgicalProcedureCoverage;
  percentageValue?: number | string;
}

const SurgicalProcedureRow = ({ procedure, percentageValue = 0 }: SurgicalProcedureRowProps) => {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();

  // Ensure we have a valid ID
  if (!procedure._id) {
    return null;
  }

  const handleRowClick = (e: React.MouseEvent) => {
    // Only expand/collapse if not clicking on the expand button
    const target = e.target as HTMLElement;
    if (
      !target.closest('button') &&
      !target.closest('[role="button"]') &&
      !target.closest('.MuiIconButton-root')
    ) {
      setExpanded(!expanded);
    }
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setExpanded(!expanded);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-HN', {
      style: 'currency',
      currency: 'HNL',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Calculate total with growth simulation percentage
  const calculateTotalWithGrowth = (baseTotal: number) => {
    const percentage =
      typeof percentageValue === 'string' ? parseFloat(percentageValue) || 0 : percentageValue || 0;
    return baseTotal * (1 + percentage / 100);
  };

  const displayTotal = procedure.total ? calculateTotalWithGrowth(procedure.total) : null;

  return (
    <>
      <TableRow
        hover
        onClick={handleRowClick}
        sx={{
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          borderBottom: `1px solid ${theme.palette.divider}`,
          '&:hover': {
            backgroundColor: `${theme.palette.primary.main}08`,
            transform: 'translateY(-1px)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
          '&:last-child td': {
            borderBottom: 'none',
          },
        }}
      >
        {/* Código CPT - Primera columna */}
        <TableCell
          sx={{
            width: '150px',
            minWidth: '150px',
            maxWidth: '150px',
            overflow: 'hidden',
            paddingLeft: 2,
            paddingRight: 2,
            py: 2.5,
            textAlign: 'left',
          }}
        >
          {procedure.cptCode ? (
            <Chip
              label={procedure.cptCode}
              size='small'
              color='primary'
              variant='outlined'
              sx={{
                fontWeight: 600,
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                  backgroundColor: `${theme.palette.primary.main}10`,
                },
              }}
            />
          ) : (
            <Typography variant='body2' color='text.secondary'>
              -
            </Typography>
          )}
        </TableCell>
        {/* Procedimiento - Segunda columna */}
        <TableCell
          sx={{
            width: '400px',
            minWidth: '400px',
            maxWidth: '400px',
            overflow: 'hidden',
            paddingLeft: 2,
            paddingRight: 2,
            py: 2.5,
            textAlign: 'left',
          }}
        >
          <Typography
            variant='body1'
            fontWeight={600}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'normal',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              wordBreak: 'break-word',
              color: 'text.primary',
              lineHeight: 1.5,
            }}
          >
            {procedure.procedureName}
          </Typography>
        </TableCell>
        {/* Total - Tercera columna */}
        <TableCell
          sx={{
            textAlign: 'right',
            fontWeight: 700,
            fontSize: '1rem',
            color: 'primary.main',
            py: 2.5,
            paddingRight: 2,
          }}
        >
          {displayTotal ? formatCurrency(displayTotal) : '-'}
        </TableCell>
        <TableCell sx={{ width: '60px', textAlign: 'center', py: 2.5 }} onClick={handleExpandClick}>
          <IconButton
            size='small'
            onClick={handleExpandClick}
            sx={{
              pointerEvents: 'auto',
              color: 'primary.main',
              transition: 'transform 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: `${theme.palette.primary.main}15`,
                transform: 'scale(1.1)',
              },
            }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={4} sx={{ py: 0, borderBottom: 'none', backgroundColor: 'transparent' }}>
          <Collapse in={expanded} timeout='auto' unmountOnExit>
            <Box
              sx={{
                p: 4,
                m: 2,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.primary.main}05 100%)`,
                border: `1px solid ${theme.palette.primary.main}20`,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              }}
            >
              {procedure.description && (
                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: `${theme.palette.primary.main}08`,
                    borderLeft: `4px solid ${theme.palette.primary.main}`,
                  }}
                >
                  <Typography variant='body1' color='text.primary' sx={{ lineHeight: 1.7 }}>
                    {procedure.description}
                  </Typography>
                </Box>
              )}

              {/* Info Chips */}
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
                {procedure.cie10Code && (
                  <Chip
                    icon={<LocalHospitalIcon sx={{ fontSize: '1rem !important' }} />}
                    label={`CIE-10: ${procedure.cie10Code}`}
                    size='small'
                    variant='outlined'
                    sx={{
                      fontWeight: 500,
                      borderColor: theme.palette.info.main,
                      color: theme.palette.info.main,
                    }}
                  />
                )}
                {procedure.surgeon && (
                  <Chip
                    icon={<PersonIcon sx={{ fontSize: '1rem !important' }} />}
                    label={procedure.surgeon}
                    size='small'
                    variant='outlined'
                    sx={{
                      fontWeight: 500,
                      borderColor: theme.palette.secondary.main,
                      color: theme.palette.secondary.main,
                    }}
                  />
                )}
                {procedure.anesthesiaType && (
                  <Chip
                    label={procedure.anesthesiaType}
                    size='small'
                    variant='outlined'
                    sx={{
                      fontWeight: 500,
                      borderColor: theme.palette.warning.main,
                      color: theme.palette.warning.main,
                    }}
                  />
                )}
                {procedure.hospitalDays && (
                  <Chip
                    icon={<CalendarTodayIcon sx={{ fontSize: '1rem !important' }} />}
                    label={procedure.hospitalDays}
                    size='small'
                    variant='outlined'
                    sx={{
                      fontWeight: 500,
                      borderColor: theme.palette.success.main,
                      color: theme.palette.success.main,
                    }}
                  />
                )}
                {procedure.surgicalHours && (
                  <Chip
                    icon={<AccessTimeIcon sx={{ fontSize: '1rem !important' }} />}
                    label={procedure.surgicalHours}
                    size='small'
                    variant='outlined'
                    sx={{
                      fontWeight: 500,
                      borderColor: theme.palette.info.main,
                      color: theme.palette.info.main,
                    }}
                  />
                )}
                <Chip
                  label={`v${procedure.version}`}
                  size='small'
                  variant='outlined'
                  sx={{
                    fontWeight: 500,
                    borderColor: theme.palette.text.secondary,
                    color: theme.palette.text.secondary,
                  }}
                />
              </Box>

              <Divider sx={{ my: 3 }} />
              {procedure.sections && procedure.sections.length > 0 && (
                <Box>
                  <Typography
                    variant='h6'
                    fontWeight={700}
                    gutterBottom
                    sx={{
                      mb: 3,
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    Secciones de Cobertura
                    <Chip
                      label={procedure.sections.length}
                      size='small'
                      color='primary'
                      sx={{ fontWeight: 600 }}
                    />
                  </Typography>

                  {procedure.sections.map((section, sectionIndex) => (
                    <Box
                      key={sectionIndex}
                      sx={{
                        mt: 3,
                        mb: 3,
                        p: 2.5,
                        borderRadius: 2,
                        backgroundColor: 'white',
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 2,
                        }}
                      >
                        <Typography
                          variant='subtitle1'
                          fontWeight={700}
                          color='primary'
                          sx={{ fontSize: '1.1rem' }}
                        >
                          {section.name}
                        </Typography>
                        {section.subtotal && (
                          <Chip
                            label={formatCurrency(calculateTotalWithGrowth(section.subtotal))}
                            size='medium'
                            color='primary'
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.95rem',
                              height: '32px',
                            }}
                          />
                        )}
                      </Box>

                      {section.items && section.items.length > 0 && (
                        <Box
                          sx={{
                            display: 'grid',
                            gap: 1,
                            mt: 2,
                          }}
                        >
                          {section.items.map((item, itemIndex) => (
                            <Box
                              key={itemIndex}
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                p: 1.5,
                                borderRadius: 1,
                                backgroundColor: `${theme.palette.grey[50]}`,
                                border: `1px solid ${theme.palette.divider}`,
                                transition: 'all 0.15s ease-in-out',
                                '&:hover': {
                                  backgroundColor: `${theme.palette.primary.main}05`,
                                  borderColor: theme.palette.primary.light,
                                },
                              }}
                            >
                              <Typography
                                variant='body2'
                                sx={{
                                  flex: 1,
                                  fontWeight: 500,
                                  color: 'text.primary',
                                }}
                              >
                                {item.detail}
                              </Typography>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.5,
                                  ml: 2,
                                }}
                              >
                                <Typography
                                  variant='body2'
                                  color='text.secondary'
                                  sx={{ minWidth: '80px', textAlign: 'right' }}
                                >
                                  {item.unit} x{' '}
                                  {formatCurrency(calculateTotalWithGrowth(item.cost))}
                                </Typography>
                                <Typography
                                  variant='body2'
                                  fontWeight={700}
                                  color='primary.main'
                                  sx={{
                                    minWidth: '120px',
                                    textAlign: 'right',
                                    fontSize: '0.95rem',
                                  }}
                                >
                                  = {formatCurrency(calculateTotalWithGrowth(item.total))}
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ))}

                  {procedure.total && (
                    <Box
                      sx={{
                        mt: 4,
                        pt: 3,
                        borderTop: `3px solid ${theme.palette.primary.main}`,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.primary.main}05 100%)`,
                        borderRadius: 2,
                        p: 3,
                      }}
                    >
                      <Typography
                        variant='h5'
                        fontWeight={800}
                        color='primary.main'
                        align='right'
                        sx={{
                          letterSpacing: '-0.02em',
                        }}
                      >
                        Total:{' '}
                        {displayTotal
                          ? formatCurrency(displayTotal)
                          : formatCurrency(procedure.total || 0)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default SurgicalProcedureRow;
